import type { ReactNode } from 'react';
import { View } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { CaseFooter, ClinicalCaseBlock } from '@/components/chat/bubbles/ClinicalCaseBlock';
import { hasClinicalCaseToShow } from '@/components/chat/bubbles/caseSummaryState';
import { HStack } from '@/components/ui/hstack';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { useRecommendedCaseLayout } from '@/hooks/chat/useRecommendedCaseLayout';
import type { ClinicalCase } from '@/types/clinicalCase';

/**
 * Says who wrote what sits under it. Without it a reader took the reworded question for the
 * sender repeating herself, and could not tell it was not part of her message.
 */
export const SUMMARY_SECTION_CAPTION = 'Summarized by Primum';

/** The recommended layout's caption: one label in one form, which also covers the case row. */
export const SUMMARY_SECTION_CAPTION_RECOMMENDED = 'Summarized by Primum AI';

export { hasClinicalCaseToShow };

type PrimumSummarySectionProps = {
  position: 'left' | 'right';
  conversationId: string;
  /** Set on the opening message only: the submitter's case, first in the section. */
  clinicalCase?: ClinicalCase;
  /** The extracted question cards, under the case. */
  children?: ReactNode;
};

/**
 * Everything Primum generated from a message, below the message itself: a rule, one caption, then
 * the case summary and the extracted questions. The submitter's text stays untouched above it,
 * and the case and the question read together under one AI label.
 */
export function PrimumSummarySection({
  position,
  conversationId,
  clinicalCase,
  children,
}: PrimumSummarySectionProps) {
  const isRecommended = useRecommendedCaseLayout(clinicalCase);

  const caption = isRecommended ? SUMMARY_SECTION_CAPTION_RECOMMENDED : SUMMARY_SECTION_CAPTION;

  const dividerClassName =
    position === 'left' ? 'border-t border-outline-300' : 'border-t border-primary-300';

  const captionColorClassName =
    position === 'left' ? 'text-typography-700' : 'text-typography-white';

  if (isRecommended) {
    return (
      <RecommendedSection
        position={position}
        conversationId={conversationId}
        clinicalCase={clinicalCase}>
        {children}
      </RecommendedSection>
    );
  }

  return (
    <VStack className="w-full" testID="primum-summary-section">
      <View className={twJoin('mt-2', dividerClassName)} />
      <Text size="xs" className={twJoin('mt-2', captionColorClassName)}>
        {caption}
      </Text>
      {clinicalCase ? (
        <ClinicalCaseBlock clinicalCase={clinicalCase} conversationId={conversationId} />
      ) : null}
      {children}
    </VStack>
  );
}

/**
 * The recommended layout (see `caseLayoutSwitch`): one bubble with a real outline, the message on
 * the gray above, and this whole section on white below a full-width divider, bled out to the
 * bubble's edges past the bottom container's padding. The label row is just the caption, then the case
 * line, then the questions hang under it, and the feedback (thumbs, then the follow-up) closes the section. Temporary with the switch.
 */
function RecommendedSection({ conversationId, clinicalCase, children }: PrimumSummarySectionProps) {
  const hasWrittenCase = clinicalCase?.status === 'written' && clinicalCase.line.trim().length > 0;

  return (
    <VStack
      className="-mx-3 -mb-3 mt-2 self-stretch border-t border-outline-300 bg-background-0 px-3 pb-3 pt-2.5"
      testID="primum-summary-section">
      <HStack className="min-h-6 items-center justify-between gap-2">
        <HStack className="flex-1 flex-wrap items-center gap-x-1">
          <Text size="xs" className="font-medium text-typography-700">
            {SUMMARY_SECTION_CAPTION_RECOMMENDED}
          </Text>
        </HStack>
      </HStack>
      {clinicalCase ? (
        <VStack className="pt-2.5">
          <ClinicalCaseBlock
            clinicalCase={clinicalCase}
            conversationId={conversationId}
            position="left"
          />
        </VStack>
      ) : null}
      {children ? <VStack className={hasWrittenCase ? 'pt-3.5' : 'pt-1'}>{children}</VStack> : null}
      {hasWrittenCase ? (
        <HStack className="max-w-full flex-wrap items-center justify-end gap-x-3 gap-y-1 self-end pt-3">
          <CaseFooter conversationId={conversationId} source="message" part="followUp" />
          <CaseFooter conversationId={conversationId} source="message" part="thumbs" />
        </HStack>
      ) : null}
    </VStack>
  );
}
